/**
 * API: proyectos / expedientes (Sprint 5+).
 *
 * Incluye:
 * - CRUD del expediente + checklist + transiciones de status.
 * - Documentos del expediente (upload, delete, download).
 * - Export PDF.
 * - Banner global "BPIN faltante post-radicación".
 * - Etiquetas y orden de presentacion de los estados. Las transiciones y el
 *   BPIN pendiente los decide el backend en el detalle (issue #823).
 */

import { ApiError, apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken, request } from "./_client";

// --- Tipos del dominio ---

export type ProjectStatus =
  "draft" | "submitted" | "eligible" | "approved" | "disbursed" | "closed" | "rejected";

export interface ChecklistItemDTO {
  id: string;
  title: string;
  description: string | null;
  is_required: boolean;
  is_completed: boolean;
  order_index: number;
  /** Sprint 10 · 2: documento adjunto (si existe). */
  linked_document_id: string | null;
  linked_document_filename: string | null;
}

export interface DocumentDTO {
  id: string;
  project_id: string;
  document_type: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  sha256_hash: string;
  version: number;
  uploaded_at: string;
}

export interface StatusHistoryEntryDTO {
  from_status: ProjectStatus | null;
  to_status: ProjectStatus;
  trigger: string;
  notes: string | null;
  created_at: string;
}

export interface ProjectDTO {
  id: string;
  opportunity_id: string | null;
  opportunity_title: string | null;
  name: string;
  /**
   * Sprint 11 · 5: contexto libre del expediente que el funcionario
   * edita desde la UI. Default `null` para retrocompat con DTOs
   * antiguos que no traían el campo.
   */
  description?: string | null;
  sector: string;
  amount_requested_cop: string | null;
  status: ProjectStatus;
  status_since: string;
  created_at: string;
  /**
   * Sprint 10: BPIN (14 dígitos). Se registra cuando el proyecto se
   * radica en PIIP. Habilita el monitoreo de SIIF Nación → primer giro
   * → cobro de comisión.
   */
  bpin: string | null;
  /**
   * Read-only: lo poblará un Celery task que monitoree SIIF Nación
   * (Sprint 11+). Hoy queda como campo expuesto para que el frontend
   * lo lea sin cambios cuando el task lo escriba.
   */
  first_disbursed_at: string | null;
}

export interface ProjectDetailDTO extends ProjectDTO {
  checklist: ChecklistItemDTO[];
  status_history: StatusHistoryEntryDTO[];
  /**
   * Cierre de la convocatoria de origen (`YYYY-MM-DD`, issue #629). `null`
   * en los idea-first y en convocatorias sin fecha; opcional para las
   * respuestas anteriores al campo.
   */
  opportunity_closes_at?: string | null;
  /**
   * La convocatoria de origen recibe postulaciones "hasta agotar cupos"
   * (issue #692): no tiene fecha de cierre y el expediente lo dice.
   */
  opportunity_until_quota_exhausted?: boolean;
  /**
   * Estados a los que puede pasar, de la maquina de estados del backend
   * (issue #823). Llegan en orden alfabetico; la UI decide como mostrarlos.
   */
  allowed_transitions: ProjectStatus[];
  /**
   * Radicado sin BPIN: la regla del banner global, calculada en el backend
   * (issue #823).
   */
  bpin_pending: boolean;
}

export interface BpinPendingProjectDTO {
  id: string;
  name: string;
  status: ProjectStatus;
}

export interface BpinPendingSummaryDTO {
  count: number;
  /** Hasta 5 proyectos para mostrar en el banner / tooltip. */
  projects: BpinPendingProjectDTO[];
}

export interface CreateProjectPayload {
  opportunity_id: string;
  name: string;
  amount_requested_cop?: string;
}

/**
 * Whitelist de sectores Kaptar — espejo FE de `subsystems.matching.KAPTAR_SECTORS`
 * (backend/subsystems/matching/engine.py, `_SECTOR_DEFICIT_RULES`). Fuente
 * unica ahi; si el backend agrega/quita un sector, esta lista se actualiza a mano.
 *
 * Formulador slice 2 (ADR-021): puebla el `<select>` del selector de sector
 * que se ofrece a expedientes idea-first (`sector === 'generico'`) para
 * habilitar el rango de presupuesto data-grounded del módulo `presupuesto`.
 */
export const KAPTAR_SECTOR_OPTIONS: { value: string; label: string }[] = [
  { value: "agua", label: "Agua potable" },
  { value: "saneamiento", label: "Saneamiento básico" },
  { value: "energia", label: "Energía" },
  { value: "conectividad", label: "Conectividad / TIC" },
  { value: "educacion", label: "Educación" },
  { value: "salud", label: "Salud" },
  { value: "seguridad", label: "Seguridad" },
  { value: "vias", label: "Vías e infraestructura" },
  { value: "vivienda", label: "Vivienda" },
  { value: "agricultura", label: "Agricultura" },
  { value: "emprendimiento", label: "Emprendimiento" },
  { value: "turismo", label: "Turismo" },
  { value: "ambiente", label: "Ambiente" },
  { value: "cultura", label: "Cultura" },
  { value: "cooperacion", label: "Cooperación internacional" },
  { value: "ciencia_tecnologia", label: "Ciencia, tecnología e innovación" },
];

/**
 * Rotulo humano de una clave de sector: "ciencia_tecnologia" se lee
 * "Ciencia, tecnología e innovación". Una clave fuera del catalogo sale sin
 * guion bajo, como en el filtro del Radar (#783). Issue #880.
 */
export function sectorLabel(sector: string): string {
  return KAPTAR_SECTOR_OPTIONS.find((s) => s.value === sector)?.label ?? sector.replace(/_/g, " ");
}

// --- CRUD del expediente ---

/**
 * Una pagina de expedientes, mas recientes primero. Sin `limit` el backend
 * devuelve 50 (maximo 200) y no manda el total: quien necesite la lista
 * completa pagina con `offset` (#882).
 */
export function fetchProjects(
  municipality: string,
  page: { limit?: number; offset?: number } = {},
): Promise<ProjectDTO[]> {
  const params = new URLSearchParams({ municipality });
  if (page.limit !== undefined) params.set("limit", String(page.limit));
  if (page.offset !== undefined) params.set("offset", String(page.offset));
  return request<ProjectDTO[]>(`/projects?${params.toString()}`);
}

export function fetchProjectDetail(id: string, municipality: string): Promise<ProjectDetailDTO> {
  return request<ProjectDetailDTO>(
    `/projects/${encodeURIComponent(id)}?municipality=${encodeURIComponent(municipality)}`,
  );
}

export function createProject(
  payload: CreateProjectPayload,
  municipality: string,
): Promise<ProjectDetailDTO> {
  return request<ProjectDetailDTO>(`/projects?municipality=${encodeURIComponent(municipality)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Edición parcial del expediente (Sprint 10; `sector` agregado en Formulador
 * slice 2 — ADR-021, permite fijar el sector real de un expediente idea-first
 * que nace en 'generico').
 *
 * Acepta cualquier subset de {name, description, amount_requested_cop, bpin, sector}.
 * Los campos no incluidos NO se tocan en backend (COALESCE).
 */
export function updateProject(
  id: string,
  municipality: string,
  payload: {
    name?: string;
    description?: string;
    amount_requested_cop?: string;
    bpin?: string;
    sector?: string;
  },
): Promise<ProjectDetailDTO> {
  return request<ProjectDetailDTO>(
    `/projects/${encodeURIComponent(id)}?municipality=${encodeURIComponent(municipality)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function changeProjectStatus(
  id: string,
  municipality: string,
  payload: { to_status: ProjectStatus; notes?: string },
): Promise<ProjectDetailDTO> {
  return request<ProjectDetailDTO>(
    `/projects/${encodeURIComponent(id)}/status?municipality=${encodeURIComponent(municipality)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function toggleChecklistItem(
  projectId: string,
  itemId: string,
  municipality: string,
  isCompleted: boolean,
): Promise<ChecklistItemDTO> {
  return request<ChecklistItemDTO>(
    `/projects/${encodeURIComponent(projectId)}/checklist/${encodeURIComponent(itemId)}?municipality=${encodeURIComponent(municipality)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_completed: isCompleted }),
    },
  );
}

// --- BPIN pendientes (banner global Sprint 11) ---

/**
 * Resumen de proyectos del tenant radicados (status >= submitted) sin BPIN
 * registrado. El AppShell lo consulta y, si `count > 0`, pinta un banner
 * persistente arriba para que el usuario complete los BPINs pendientes.
 */
export function fetchBpinPendingSummary(municipality: string): Promise<BpinPendingSummaryDTO> {
  return request<BpinPendingSummaryDTO>(
    `/projects/bpin-pending-summary?municipality=${encodeURIComponent(municipality)}`,
  );
}

// --- Documentos del expediente ---

/** Sube un archivo al expediente. Si checklistItemId va, vincula al item. */
export async function uploadProjectDocument(args: {
  projectId: string;
  file: File;
  dane: string;
  checklistItemId?: string;
  documentType?: string;
}): Promise<DocumentDTO> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Auth requerida.");
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);

  const form = new FormData();
  form.append("file", args.file);
  form.append("document_type", args.documentType ?? "general");
  if (args.checklistItemId) {
    form.append("checklist_item_id", args.checklistItemId);
  }

  const url = `${API_V1}/projects/${encodeURIComponent(args.projectId)}/documents?dane=${encodeURIComponent(args.dane)}`;
  const res = await safeFetch(url, { method: "POST", headers, body: form });

  if (res.status === 401) await supabase.auth.signOut();
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }

  return (await res.json()) as DocumentDTO;
}

/** Borra un documento. Idempotente. */
export async function deleteProjectDocument(args: {
  documentId: string;
  dane: string;
}): Promise<void> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Auth requerida.");
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);

  const url = `${API_V1}/documents/${encodeURIComponent(args.documentId)}?dane=${encodeURIComponent(args.dane)}`;
  const res = await safeFetch(url, { method: "DELETE", headers });

  if (res.status === 401) await supabase.auth.signOut();
  if (!res.ok && res.status !== 204) {
    throw await apiErrorFromResponse(res);
  }
}

/**
 * URL del endpoint de descarga. Hace 302 a la signed URL de Supabase
 * (15min TTL) pero exige `Authorization`, así que **no** sirve como
 * `href` de un `<a>`: una navegación del browser no lleva el JWT y el
 * backend responde 401 antes del 302 (issue #550). Usar
 * `downloadProjectDocument`.
 */
function buildDocumentDownloadUrl(args: { documentId: string; dane: string }): string {
  return `${API_V1}/documents/${encodeURIComponent(args.documentId)}/download?dane=${encodeURIComponent(args.dane)}`;
}

/**
 * Descarga el documento adjunto a un item del checklist (issue #550).
 *
 * Mismo patrón que `downloadProjectPdf`: `fetch` con `Authorization`,
 * el browser sigue el 302 a la signed URL de Supabase y el binario
 * llega como blob (directo desde Supabase, sin pasar por el backend).
 * Se dispara como save-as con el nombre original que subió el
 * funcionario; no usamos el `content-disposition` de Supabase porque
 * trae el storage key, no el nombre del archivo.
 */
export async function downloadProjectDocument(args: {
  documentId: string;
  dane: string;
  filename: string;
}): Promise<void> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError(
      401,
      "Tu sesión expiró. Vuelve a iniciar sesión para descargar el documento.",
    );
  }
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);

  const res = await safeFetch(buildDocumentDownloadUrl(args), { method: "GET", headers });

  if (res.status === 401) await supabase.auth.signOut();
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }

  triggerBlobDownload(await res.blob(), args.filename);
}

/**
 * Descarga el PDF del expediente y dispara save-as en el browser.
 *
 * No usamos `<a href>` directo porque el endpoint requiere
 * `Authorization: Bearer <token>` (no se puede inyectar en una
 * navegación). Hacemos `fetch` con header, leemos blob y disparamos
 * un click sintético en un anchor temporal.
 */
export async function downloadProjectPdf(args: {
  projectId: string;
  dane: string;
  fallbackName?: string;
}): Promise<void> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Auth requerida.");
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);

  const url = `${API_V1}/projects/${encodeURIComponent(args.projectId)}/export.pdf?municipality=${encodeURIComponent(args.dane)}`;
  const res = await safeFetch(url, { method: "GET", headers });

  if (res.status === 401) await supabase.auth.signOut();
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }

  const blob = await res.blob();
  const filename =
    parseFilenameFromContentDisposition(res.headers.get("content-disposition")) ??
    args.fallbackName ??
    `expediente_${args.projectId.slice(0, 8)}.pdf`;
  triggerBlobDownload(blob, filename);
}

/**
 * Dispara save-as en el browser con un click sintético sobre un anchor
 * temporal apuntando al blob. Compartido por las descargas autenticadas
 * (PDF del expediente, documentos adjuntos).
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Liberamos la URL después de un tick — algunos browsers la siguen leyendo
  // mientras procesan el click.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

// --- Estados: presentacion ---
//
// Que transiciones tiene un expediente lo decide el backend y llega en
// `ProjectDetailDTO.allowed_transitions` (issue #823). Aca solo vive como
// se muestran.

/**
 * Orden del ciclo de vida para presentar estados: el camino feliz primero y
 * rechazado al final.
 */
const STATUS_ORDER: readonly ProjectStatus[] = [
  "draft",
  "submitted",
  "eligible",
  "approved",
  "disbursed",
  "closed",
  "rejected",
];

/** Copia de `statuses` en el orden del ciclo de vida. */
export function sortByLifecycle(statuses: readonly ProjectStatus[]): ProjectStatus[] {
  return [...statuses].sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b));
}

/**
 * Helper: dada la transición desde el estado actual a `next`, ¿es
 * un "ir hacia atrás" (corregir estado) o el camino forward natural?
 *
 * Lo usamos para pintar las dos opciones en grupos distintos en la
 * UI — "Avanzar a X" vs "Volver a Borrador". Sin esto, todos los
 * botones se mezclan y el usuario no sabe cuál corresponde al
 * camino feliz.
 */
export function isReverseTransition(from: ProjectStatus, to: ProjectStatus): boolean {
  // El único reverse intencional es * → draft (cuando from no es
  // draft mismo). rejected→draft también cae acá pero es esperado.
  return to === "draft" && from !== "draft" && from !== "rejected";
}

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "Borrador",
  submitted: "Radicado",
  eligible: "Elegible",
  approved: "Aprobado",
  disbursed: "Desembolsado",
  closed: "Cerrado",
  rejected: "Rechazado",
};
